import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import Activity from "../../components/Activity";

const API_BASE = process.env.REACT_APP_ACCOUNTS_API_URL || "https://api.accounts.clikkle.com/api";
const getToken = () => localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken") || localStorage.getItem("token") || "";
const short = (value, length = 44) => value && value.length > length ? `${value.slice(0, length)}…` : value || "—";

export default function IdentityClientActivity() {
  const [payload, setPayload] = useState({ summary: { accepted: 0, blocked: 0 }, events: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const response = await fetch(`${API_BASE}/auth/security/client-events?limit=150`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || "Identity activity could not be loaded");
      setPayload(data);
    } catch (requestError) { setError(requestError.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const blockedRate = useMemo(() => {
    const total = payload.summary.accepted + payload.summary.blocked;
    return total ? Math.round((payload.summary.blocked / total) * 100) : 0;
  }, [payload]);
  return <Box>
    <Activity />
    <Box sx={{ px: 2, pb: 4 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} spacing={2} mb={2}>
        <Box><Typography variant="h4" fontWeight={700}>Identity Client Activity</Typography><Typography color="text.secondary">Verified applications, blocked legacy clients, redirect failures and protocol enforcement.</Typography></Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
        <Paper sx={{ p: 2, minWidth: 190 }}><Typography color="text.secondary">Accepted · 24h</Typography><Typography variant="h4" color="success.main">{payload.summary.accepted}</Typography></Paper>
        <Paper sx={{ p: 2, minWidth: 190 }}><Typography color="text.secondary">Blocked · 24h</Typography><Typography variant="h4" color="error.main">{payload.summary.blocked}</Typography></Paper>
        <Paper sx={{ p: 2, minWidth: 190 }}><Typography color="text.secondary">Blocked rate</Typography><Typography variant="h4">{blockedRate}%</Typography></Paper>
      </Stack>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      <Paper sx={{ overflow: "hidden" }}>
        {loading ? <Box p={6} textAlign="center"><CircularProgress /></Box> : <Box sx={{ overflowX: "auto" }}><Table size="small">
          <TableHead><TableRow><TableCell>Time</TableCell><TableCell>Result</TableCell><TableCell>Verified client</TableCell><TableCell>Claimed client</TableCell><TableCell>Protocol</TableCell><TableCell>Origin / redirect</TableCell><TableCell>Reason</TableCell><TableCell>Correlation ID</TableCell></TableRow></TableHead>
          <TableBody>{payload.events.map((event) => <TableRow key={event._id} hover>
            <TableCell sx={{ whiteSpace: "nowrap" }}>{new Date(event.createdAt).toLocaleString()}</TableCell>
            <TableCell><Chip size="small" color={event.result === "accepted" ? "success" : "error"} label={event.result} /></TableCell>
            <TableCell>{event.clientId || "—"}</TableCell><TableCell>{event.claimedClientId || "—"}</TableCell><TableCell>{event.protocolVersion ?? "—"}</TableCell>
            <TableCell><Typography variant="body2">{short(event.origin)}</Typography><Typography variant="caption" color="text.secondary">{short(event.redirectUri)}</Typography></TableCell>
            <TableCell><Typography variant="body2">{event.reasonCode || "—"}</Typography><Typography variant="caption" color="text.secondary">{event.reason || ""}</Typography></TableCell>
            <TableCell><code>{event.correlationId}</code></TableCell>
          </TableRow>)}</TableBody>
        </Table></Box>}
      </Paper>
    </Box>
  </Box>;
}
